import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { default as axios } from '../axiosConfig';
import { Typography, Chip } from '@mui/material';
import { AppliedIcon, Interview1Icon, Interview2Icon, Interview3Icon } from './CustomIcons';
import CancelIcon from '@mui/icons-material/Cancel';
import { CheckCircle } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEdit, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { EllipsisStatusMenu } from './EllipsisStatusContent';
import {
	API_GetApplicantDetails,
	API_GetApplicantJobs,
	API_UpdateCandidateDetails,
	API_UpdateCandidateInterviewRemarks,
	API_UpdateCandidateInterviewStage,
	API_UpdateCandidateRemarks,
	API_PeopleProfileUpload,
} from '../Assets/ApiUrlContainer';
import PeopleProfileUpload from './PeopleProfileUpload';
import UserContext from '../Contexts/UserContext';

const ApplicantDetailsPage = () => {
	const { applicantId } = useParams();
	const { user } = useContext(UserContext);
	const isAdmin = user?.RoleId === 1;

	const [applicant, setApplicant] = useState(null);
	const [loading, setLoading] = useState(true);

	const [isEdittingApplicantDetails, setIsEdittingApplicantDetails] = useState(false);
	const [phone, setPhone] = useState('');
	const [summary, setSummary] = useState('');
	const [location, setLocation] = useState('');
	const [linkedIn, setLinkedIn] = useState('');
	const [website, setWebsite] = useState('');
	const [experience, setExperience] = useState('');
	const [candidateType, setCandidateType] = useState('');
	const [workPermit, setWorkPermit] = useState('');
	const [availability, setAvailability] = useState('');

	const [remarks, setRemarks] = useState(''); // General remarks
	const [editingRemarks, setEditingRemarks] = useState(false); // For general remarks
	const [remarksEntryText, setRemarksEntryText] = useState('');

	const [jobs, setJobs] = useState([]); // Holds job applications
	const [jobRemarksEntryText, setJobRemarksEntryText] = useState('');
	const [editingJobId, setEditingJobId] = useState(null); // Track which job is being edited
	const [openEllipsisMenuJobId, setOpenEllipsisMenuJobId] = useState(null);
	const [selectedJob, setSelectedJob] = useState(null); // For selected job
	const [peopleProfileLink, setPeopleProfileLink] = useState('');

	const [showMoreToggled, setShowMoreToggled] = useState(false);

	const fetchApplicantDetails = useCallback(async () => {
		try {
			const response = await axios.get(`${API_GetApplicantDetails}/${applicantId}`);
			// console.log('API Response:', response.data); // Debugging: check what the API returns

			setApplicant(response.data.CandidateInfo);
			setRemarks(response.data.CandidateInfo.Remarks || '');
			setPeopleProfileLink(response.data.PeopleProfileUrl || '');
			setLoading(false);
		} catch (error) {
			console.error('Error fetching applicant details:', error);
		} finally {
			setLoading(false);
		}
	}, [applicantId]);

	const fetchApplicantJobs = useCallback(async () => {
		try {
			const response = await axios.get(`${API_GetApplicantJobs}/${applicantId}`);
			setJobs(response.data);
		} catch (error) {
			console.error('Error fetching applicant jobs:', error);
		}
	}, [applicantId]);

	useEffect(() => {
		fetchApplicantDetails();
		fetchApplicantJobs();
	}, [fetchApplicantDetails, fetchApplicantJobs]);

	const handleEditApplicantDetails = () => {
		setIsEdittingApplicantDetails(!isEdittingApplicantDetails);
		setPhone(applicant.Phone || '');
		setSummary(applicant.Summary || '');
		setLocation(applicant.Location || '');
		setLinkedIn(applicant.LinkedIn || '');
		setWebsite(applicant.Website || '');
		setExperience(applicant.Experience || '');
		setCandidateType(applicant.State || '');
		setWorkPermit(applicant.ValidWorkPermit || null);
		setAvailability(applicant.EmploymentTypeTarget || '');
		setPeopleProfileLink(peopleProfileLink || '');
	};

	const handleCloseEditDetails = () => {
		setIsEdittingApplicantDetails(false);
		setPhone('');
		setSummary('');
		setLocation('');
		setLinkedIn('');
		setWebsite('');
		setExperience('');
		setCandidateType('');
		setWorkPermit(null);
		setAvailability('');
		setPeopleProfileLink(peopleProfileLink);
	};

	const handleSaveEditDetails = async () => {
		try {
			await axios.put(API_UpdateCandidateDetails, {
				applicantId: applicant.Id,
				phone,
				summary,
				location,
				linkedIn,
				website,
				experience,
				candidateType,
				workPermit,
				availability,
				peopleProfileLink,
			});

			fetchApplicantDetails();
			handleCloseEditDetails();
		} catch (error) {
			console.error(error);
		}
	};

	const handleSaveRemarks = async () => {
		try {
			let remarksResponse = await axios.put(API_UpdateCandidateRemarks, {
				ApplicantId: applicant.Id,
				Remarks: remarksEntryText,
			});

			setRemarks(remarksResponse.data.Remarks);
			setEditingRemarks(false);
		} catch (error) {
			console.error('Error saving remarks:', error);
			// alert('Failed to save remarks. Please try again.');
		}
	};

	const handleSaveJobRemarks = async (job) => {
		await axios.put(API_UpdateCandidateInterviewRemarks, {
			InterviewId: job.InterviewId,
			Notes: jobRemarksEntryText,
		});

		await fetchApplicantJobs();
		setJobRemarksEntryText('');
		setEditingJobId(null);
	};

	// Ensure we default to an empty string if job.Remarks is null
	const handleEditJobRemarks = (job) => {
		setEditingJobId(job.JobId);
		setJobRemarksEntryText(job.Remarks || '');
	};

	const handleCancelEditJobRemarks = () => {
		setEditingJobId(null);
		setJobRemarksEntryText('');
	};

	const handleEditRemarks = () => {
		setEditingRemarks(true);
		setRemarksEntryText(remarks);
	};

	const handleCancelEditRemarks = () => {
		setEditingRemarks(false);
		setRemarksEntryText('');
	};

	const handleOpenEllipsisMenuClicked = (job) => {
		setOpenEllipsisMenuJobId(job.JobId);
		setSelectedJob(job);
	};

	const handleCloseEllipsisMenu = () => {
		setOpenEllipsisMenuJobId(null);
		setSelectedJob(null);
	};

	const handleEllipsisOptionSelected = async (selectedEllipsisOption) => {
		let changeType = selectedEllipsisOption.menuItem;
		let newItem = selectedEllipsisOption.listItem;

		if (changeType === 'Status') {
			await handleStatusChange(newItem);
		} else {
			await handleLabelChange(newItem);
		}

		await fetchApplicantJobs();
		handleCloseEllipsisMenu();
	};

	const handleStatusChange = async (newState) => {
		if (selectedJob) {
			try {
				await axios.patch(API_UpdateCandidateInterviewStage, {
					ApplicationId: selectedJob.ApplicationId,
					InterviewStatus: newState,
					Label: selectedJob.Label,
				});

				setJobs((prev) =>
					prev.map((job) =>
						job.ApplicationId === selectedJob.ApplicationId ? { ...job, State: newState } : job
					)
				);
			} catch (error) {
				console.error('Error updating status:', error);
				// alert('Failed to update status. Please try again.');
			}
		}
	};

	const handleLabelChange = async (newLabel) => {
		if (selectedJob) {
			try {
				await axios.patch(API_UpdateCandidateInterviewStage, {
					ApplicationId: selectedJob.ApplicationId,
					InterviewStatus: selectedJob.State,
					Label: newLabel,
				});

				setJobs((prev) =>
					prev.map((job) =>
						job.ApplicationId === selectedJob.ApplicationId ? { ...job, Label: newLabel } : job
					)
				);
			} catch (error) {
				console.error('Error updating label:', error);
				// alert('Failed to update label. Please try again.');
			}
		}
	};

	const handleShowMoreSummaryToggled = () => {
		setShowMoreToggled(!showMoreToggled);
	};

	const handleUploadSuccess = async (fileUrl) => {
		try {
			// Only update if a new file is uploaded
			if (fileUrl !== peopleProfileLink) {
				await axios.post(API_PeopleProfileUpload, {
					CandidateId: applicant.Id,
					PeopleProfileLink: fileUrl,
				});
				setPeopleProfileLink(fileUrl);
			}
		} catch (error) {
			console.error('Error updating people profile:', error);
		}
	};

	// Function to render the label chip
	const renderLabel = (label) => {
		// Do not render anything if there's no label
		if (!label) return null;

		let chipColor = ''; // Default empty
		let chipLabel = label || 'N/A'; // Default label if none

		// Determine the chip color based on label
		if (label === 'Interviewed') {
			chipColor = 'green';
		} else if (label === 'Interview Scheduled') {
			chipColor = 'orange';
		}

		return (
			<Chip
				label={chipLabel}
				style={{
					backgroundColor: 'transparent', // No background color for the chip
					color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
					fontFamily: 'var(--fontFamily, Roboto)', // Apply the font family
					fontSize: 'var(--font-size-08125-rem, 13.5px)', // Apply the font size
					fontWeight: 'var(--fontWeightRegular, 400)', // Apply the font weight
					lineHeight: '18px', // Set line height
					letterSpacing: '0.16px', // Set letter spacing
					padding: '4px 10px',
					borderColor: 'grey', // Grey border
					borderWidth: '1px',
					borderStyle: 'solid',
					height: '26px',
				}}
				icon={
					chipColor ? (
						<span
							style={{
								width: '10px',
								height: '10px',
								borderRadius: '50%',
								backgroundColor: chipColor, // Set color of the dot
								marginRight: '8px', // Space between dot and label text
							}}
						/>
					) : null
				}
			/>
		);
	};

	const ProgressCircle = (category) => {
		switch (category) {
			case 'Hired': return <CheckCircle sx={{ color: 'green' }} />;
			case 'Rejected': return <CancelIcon sx={{ color: 'red' }} />;
			case 'Interview 1': return <Interview1Icon />;
			case 'Interview 2': return <Interview2Icon />;
			case 'Interview 3': return <Interview3Icon />;
			case 'Applied':
			case 'Matched':
				return <AppliedIcon />;

			default: return <AppliedIcon />;
		}
	};

	if (loading) {
		return <Typography>Loading...</Typography>;
	}

	if (!applicant) {
		return <Typography>Applicant not found</Typography>;
	}

	const formatPhoneNumber = (input) => {
		if (!input) return ""; // Handle empty input case

		// Remove all non-numeric characters
		let cleaned = input.replace(/[^\d]/g, "");

		// If no digits exist, return an empty string
		if (cleaned.length === 0) return "";

		// Hardcoded: Add North American country code if missing
		if (!cleaned.startsWith("1")) cleaned = "1" + cleaned;

		// Limit length
		if (cleaned.length > 11) cleaned = cleaned.slice(0, 11);

		// Format the number
		let formatted = "+1";
		if (cleaned.length > 1) formatted += `-${cleaned.slice(1, 4)}`;
		if (cleaned.length > 4) formatted += `-${cleaned.slice(4, 7)}`;
		if (cleaned.length > 7) formatted += `-${cleaned.slice(7, 11)}`;

		return formatted;
	};

	const handlePhoneInputChange = (e) => {
		const rawInput = e.target.value;
		const formattedNumber = formatPhoneNumber(rawInput);
		setPhone(formattedNumber);
	};

	return (
		<>
			<div className='candidateContainer'>
				<div className='tgs-content'>
					<div className='candidateProfileInfo'>
						<div className='candidateName'>
							{applicant.Name || 'No Name'}

							{!isEdittingApplicantDetails && (
								<span className='editJobRemarks' onClick={() => handleEditApplicantDetails()}>
									<FontAwesomeIcon icon={faEdit} />
								</span>
							)}
						</div>
						<div className='candidateContactDetails'>
							<div className='leftPanel'>
								<div className='email'>
									<div className='label'>Email</div>
									<div className='labelEntry'>{applicant.Email || '-'}</div>
								</div>

								<div className='phoneNumber'>
									<div className='label'>Phone Number</div>
									{!isEdittingApplicantDetails && (
										<div className='labelEntry'>{applicant.Phone || '-'}</div>
									)}
									{isEdittingApplicantDetails && (
										<textarea value={phone} onChange={handlePhoneInputChange}></textarea>
									)}
								</div>

								<div className='summary'>
									<div className='label'>Summary</div>

									{!isEdittingApplicantDetails && (
										<div className={`labelEntry ${showMoreToggled ? 'expandedSummary' : ''}`}>
											{applicant.Summary || '-'}
										</div>
									)}
									{isEdittingApplicantDetails && (
										<textarea
											value={summary}
											onChange={(e) => setSummary(e.target.value)}
										></textarea>
									)}

									{!isEdittingApplicantDetails && applicant.Summary && (
										<div className='showMoreLessBtn'>
											<span onClick={handleShowMoreSummaryToggled}>
												{showMoreToggled ? 'Show less' : 'Show more'}
											</span>
										</div>
									)}
								</div>
							</div>

							<div className='middlePanel'>
								<div className='location'>
									<div className='label'>Location</div>

									{!isEdittingApplicantDetails && (
										<div className='labelEntry'>{applicant.Location || '-'}</div>
									)}
									{isEdittingApplicantDetails && (
										<textarea
											value={location}
											onChange={(e) => setLocation(e.target.value)}
										></textarea>
									)}
								</div>

								<div className='linkedIn'>
									<div className='label'>LinkedIn Profile</div>
									{!isEdittingApplicantDetails && (
										<div className='labelEntry'>
											{applicant.LinkedIn ? (
												<a href={applicant.LinkedIn} target='_blank' rel='noopener noreferrer'>
													{applicant.LinkedIn}
												</a>
											) : (
												'-'
											)}
										</div>
									)}
									{isEdittingApplicantDetails && (
										<textarea
											value={linkedIn}
											onChange={(e) => setLinkedIn(e.target.value)}
										></textarea>
									)}
								</div>

								<div className='website'>
									<div className='label'>Website</div>
									{!isEdittingApplicantDetails && (
										<div className='labelEntry'>
											{applicant.Website ? (
												<a href={applicant.Website} target='_blank' rel='noopener noreferrer'>
													{applicant.Website}
												</a>
											) : (
												'-'
											)}
										</div>
									)}
									{isEdittingApplicantDetails && (
										<textarea
											value={website}
											onChange={(e) => setWebsite(e.target.value)}
										></textarea>
									)}
								</div>

								<div className='experience'>
									<div className='label'>Years of Experience</div>
									{!isEdittingApplicantDetails && (
										<div className='labelEntry'>{applicant.Experience || '-'}</div>
									)}
									{isEdittingApplicantDetails && (
										<select
											value={experience || ''}
											onChange={(e) => setExperience(e.target.value)}
										>
											<option value=''>--Select--</option>
											<option value='>5 years'>&gt;5 years</option>
											<option value='2-5 years'>2-5 years</option>
											<option value='<2 years'>&lt;2 years</option>
											<option value='Student'>student</option>
										</select>
									)}
								</div>
							</div>

							<div className='rightPanel'>
								<div className='candidateType'>
									<div className='label'>Candidate Type</div>

									{!isEdittingApplicantDetails && (
										<div className='labelEntry'>{applicant.State || '-'}</div>
									)}
									{isEdittingApplicantDetails && (
										<select
											value={candidateType || ''}
											onChange={(e) => setCandidateType(e.target.value)}
										>
											<option value=''>--Select--</option>
											<option value='New'>New</option>
											<option value='Rejected'>Rejected</option>
											<option value='Resubmitted'>Resubmitted</option>
											<option value='TGS Verified'>TGS Verified</option>
											<option value='TGS Verified A'>TGS Verified A</option>
											<option value='TGS Employed'>TGS Employed</option>
										</select>
									)}
								</div>

								<div className='workPermit'>
									<div className='label'>Valid Work Permit</div>
									{!isEdittingApplicantDetails && (
										<div className='labelEntry'>
											{applicant.ValidWorkPermit === 1
												? 'Yes'
												: applicant.ValidWorkPermit === 0
													? 'No'
													: '-'}
										</div>
									)}
									{isEdittingApplicantDetails && (
										<select
											value={workPermit || ''}
											onChange={(e) =>
												setWorkPermit(e.target.value === '' ? null : Number(e.target.value))
											}
										>
											<option value=''>--Select--</option>
											<option value='0'>No</option>
											<option value='1'>Yes</option>
										</select>
									)}
								</div>

								<div className='availability'>
									<div className='label'>Availability</div>
									{!isEdittingApplicantDetails && (
										<div className='labelEntry'>{applicant.EmploymentTypeTarget || '-'}</div>
									)}
									{isEdittingApplicantDetails && (
										<select
											value={availability || ''}
											onChange={(e) => setAvailability(e.target.value)}
										>
											<option value=''>--Select--</option>
											<option value='Full-time'>Full-time</option>
											<option value='Part-time'>Part-time</option>
											<option value='Gig'>Gig</option>
										</select>
									)}
								</div>

								{/* People Profile Upload Section */}
								<div className='peopleProfile'>
									<div className='label'>People Profile</div>
									{!isEdittingApplicantDetails ? (
										(peopleProfileLink || applicant.CandidateProfileBriefing) ? (
											<div className='labelEntry'>
												<a href={peopleProfileLink ? peopleProfileLink : applicant.CandidateProfileBriefing} target='_blank' rel='noopener noreferrer'>
													View People Profile
												</a>
											</div>
										) : (
											<div className='labelEntry'>No Profile Uploaded</div>
										)
									) : (
										<PeopleProfileUpload onUploadSuccess={(url) => handleUploadSuccess(url)} />
									)}
								</div>
							</div>
						</div>

						{isEdittingApplicantDetails && (
							<div className='applicantEditDetailsButtons'>
								<button className='cancelDetailsEditBtn' onClick={handleCloseEditDetails}>
									CANCEL
								</button>

								<button className='saveDetailsBtn' onClick={handleSaveEditDetails}>
									SAVE
								</button>
							</div>
						)}
					</div>
				</div>

				<div className='tgs-content'>
					<div className='candidateResumeDownload'>
						<div className='resumeDownloadHeading'>Resume</div>
						<div className='resumeDownloadFile'>
							<div className='fileName'>
								{applicant.Resume ? (
									<a href={applicant.Resume} target='_blank' rel='noopener noreferrer'>
										{applicant.Name ? `Resume_${applicant.Name}.pdf` : 'Resume.pdf'}
									</a>
								) : (
									<div className='No resume available'></div>
								)}
							</div>

							{applicant.Resume && (
								<div className='downloadIcon' onClick={() => window.open(applicant.Resume, '_blank')}>
									<FontAwesomeIcon icon={faDownload} />
								</div>
							)}
						</div>
					</div>
				</div>

				<div className='tgs-content'>
					<div className='candidateRemarks'>
						<div className='remarksHeading'>
							<div className='heading'>
								Remarks
								{editingRemarks && (
									<div className='instructionText'>Provide a general remark or leave a comment</div>
								)}
							</div>

							{!editingRemarks && remarks.length > 0 && (
								<span onClick={handleEditRemarks}>
									<FontAwesomeIcon icon={faEdit} />
								</span>
							)}
						</div>

						{!editingRemarks && remarks.length > 0 && <div className='remarksViewContent'>{remarks}</div>}
						{!editingRemarks && remarks.length === 0 && (
							<div className='addRemarksBtnContainer'>
								<button className='addRemarksBtn' onClick={handleEditRemarks}>
									ADD REMARK
								</button>
							</div>
						)}

						{editingRemarks && (
							<div className='remarksEditContent'>
								<textarea
									value={remarksEntryText}
									onChange={(e) => setRemarksEntryText(e.target.value)}
								></textarea>

								<div className='remarksEditButtons'>
									<button className='cancelRemarksEditBtn' onClick={handleCancelEditRemarks}>
										CANCEL
									</button>

									<button className='saveRemarksBtn' onClick={handleSaveRemarks}>
										SAVE
									</button>
								</div>
							</div>
						)}
					</div>
				</div>

				<div className='tgs-content'>
					<div className='candidateApplications'>
						<div className='applicationsHeading'>Job Applications</div>
						{jobs.length === 0 && <div className='applicationDetails'>No job applications applicable</div>}
						{jobs.length > 0 &&
							jobs.map((job, index) => (
								<div className='applicationDetails' key={index}>
									<div className='jobApplicationLink'>
										<Link to={`/jobs/${job.JobId}`}>{job.Name}</Link>
									</div>

									<div className='jobApplicationStatus'>
										<div className='icon'>{ProgressCircle(job.State)}</div>
										<div className='iconStatusText'>{job.State}</div>
										{job.Label && <div className='chipColor'>{renderLabel(job.Label)}</div>}
										{isAdmin && (
											<span className='editJobState' onClick={() => handleOpenEllipsisMenuClicked(job)}>
												<FontAwesomeIcon icon={faEllipsisV} />
												<EllipsisStatusMenu
													openEllipsisMenu={openEllipsisMenuJobId === job.JobId}
													onCloseEllipsis={handleCloseEllipsisMenu}
													onItemSelected={handleEllipsisOptionSelected}
												/>
											</span>
										)}

									</div>

									{editingJobId === job.JobId && (
										<div className='instructionText'>
											Provide a general remark or leave a comment for the application interviews
										</div>
									)}

									{editingJobId !== job.JobId && job.InterviewId && job.Remarks && job.Remarks.length > 0 && (
										<div className='interviewRemarksLabel'>
											Interview Notes
											<span
												className='editJobRemarks'
												onClick={() => handleEditJobRemarks(job)}
											>
												<FontAwesomeIcon icon={faEdit} />
											</span>
										</div>
									)}

									<div className='interviewRemarksLabelEntry'>
										{editingJobId !== job.JobId && job.Remarks && job.Remarks.length > 0 && (
											<div className='jobRemarksText'>{job.Remarks}</div>
										)}

										{editingJobId !== job.JobId && job.InterviewId && (!job.Remarks || job.Remarks.length === 0) && (
											<div className='addRemarksBtnContainer'>
												<button
													className='addRemarksBtn'
													onClick={() => handleEditJobRemarks(job)}
												>
													ADD INTERVIEW REMARK
												</button>
											</div>
										)}
									</div>

									{editingJobId === job.JobId && (
										<div className='jobRemarks'>
											<textarea
												value={jobRemarksEntryText}
												onChange={(e) => setJobRemarksEntryText(e.target.value)}
											></textarea>

											<div className='remarksEditButtons'>
												<button
													className='cancelRemarksEditBtn'
													onClick={handleCancelEditJobRemarks}
												>
													CANCEL
												</button>

												<button
													className='saveRemarksBtn'
													onClick={() => handleSaveJobRemarks(job)}
												>
													SAVE
												</button>
											</div>
										</div>
									)}
								</div>
							))}
					</div>
				</div>
			</div>
		</>
	);
};

export default ApplicantDetailsPage;